import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, serverTimestamp,
  collection, getDocs, deleteDoc, Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import useUserPrefs from '../hooks/useUserPrefs';
import useSelectedDevice from '../hooks/useSelectedDevice';
import useStallRecovery from '../hooks/useStallRecovery';
import { withRetry, cachedDocs, cachedDoc } from '../utils/firestoreRetry';
import {
  deriveTankInfoFromFeet,
  validateTankInfo,
} from '../utils/cal';
import { AppearancePills } from '../components/ThemeToggle';
import PlusCard from '../components/PlusCard';
import PlusPromptModal from '../components/PlusPromptModal';
import usePlusReturn from '../hooks/usePlusReturn';
import { plusIsOn, tierOf } from '../utils/plusStatus';
import '../styles/Alerts.css';

// ── App constants ──────────────────────────────────────────────────────────

const APP_VERSION = '1.0';

// When the user saves tank info changes from Settings, set fastModeUntil
// to this many ms in the future so the device picks up new mA values
// within ~15s instead of waiting for the next normal poll cycle.
const FAST_MODE_SETTINGS_EDIT_MS = 2 * 60 * 1000;  // 2 minutes

// ── Validation rules ───────────────────────────────────────────────────────

const LIMITS = {
  locationName: { max: 32 },
  tankName:     { max: 24 },
  capacity:     { min: 0, max: 1000000 },
  depth:        { min: 0, max: 50 },
  inches:       { min: 0, max: 600 },
};

// ── LED mode options ──────────────────────────────────────────────────────

const LED_MODES = [
  {
    value: 'always_on',
    label: 'Always on',
    description: 'Glance at the unit any time.',
  },
  {
    value: 'button_30s',
    label: '30 seconds after button press',
    description: 'Press the button to see the level.',
  },
  {
    value: 'smart_notify',
    label: 'Wake on changes',
    description: 'LED will turn on when water level changes (i.e. during a fill or during a leak) or for 30 seconds after a button press',
  },
];

function validateLocationName(v) {
  if (!v.trim()) return 'Required';
  if (v.length > LIMITS.locationName.max) return `Max ${LIMITS.locationName.max} characters`;
  return null;
}
function validateTankName(v) {
  if (!v.trim()) return 'Required';
  if (v.length > LIMITS.tankName.max) return `Max ${LIMITS.tankName.max} characters`;
  return null;
}
function validateCapacity(v) {
  if (v === '' || v == null) return null;
  const n = parseFloat(v);
  if (isNaN(n)) return 'Must be a number';
  if (n < LIMITS.capacity.min) return 'Cannot be negative';
  if (n > LIMITS.capacity.max) return `Cannot exceed ${LIMITS.capacity.max.toLocaleString()} gal`;
  return null;
}

// Email validation for the Share access modal.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(v) {
  return EMAIL_RE.test((v || '').trim());
}

// ── Depth format helpers ──────────────────────────────────────────────────

function splitDepth(decimalFeetStr) {
  if (decimalFeetStr === '' || decimalFeetStr == null) {
    return { ft: '', in: '', totalIn: '', decimalFt: '' };
  }
  const n = parseFloat(decimalFeetStr);
  if (isNaN(n)) return { ft: '', in: '', totalIn: '', decimalFt: '' };
  const totalIn = n * 12;
  const ft = Math.floor(totalIn / 12);
  const inches = totalIn - ft * 12;
  const inDisplay = Math.abs(inches - Math.round(inches)) < 0.05
    ? String(Math.round(inches))
    : inches.toFixed(1);
  return {
    ft: String(ft),
    in: inDisplay,
    totalIn: Math.abs(totalIn - Math.round(totalIn)) < 0.05
      ? String(Math.round(totalIn))
      : totalIn.toFixed(1),
    decimalFt: n.toFixed(2),
  };
}

// ── Field component ───────────────────────────────────────────────────────

function Field({ label, error, children, hint, fieldRef }) {
  return (
    <div
      className={`field ${error ? 'field--error' : ''}`}
      ref={fieldRef}
    >
      <label>{label}</label>
      {children}
      {error && <div className="field-error">{error}</div>}
      {!error && hint && <div className="hint">{hint}</div>}
    </div>
  );
}

// ── Depth input ───────────────────────────────────────────────────────────
// Renders ft+in pair, inches-only, or decimal-feet based on user pref.
// Value is always a decimal-feet string internally.

function DepthInput({ valueFt, setValueFt, format, setDirty, error, fieldRef, label }) {
  const parts = splitDepth(valueFt);

  function onFtInChange(field, raw) {
    const ftN = field === 'ft' ? parseFloat(raw)    : parseFloat(parts.ft);
    const inN = field === 'in' ? parseFloat(raw)    : parseFloat(parts.in);
    const ftSafe = isNaN(ftN) ? 0 : ftN;
    const inSafe = isNaN(inN) ? 0 : inN;
    if (raw === '' && field === 'ft' && (parts.in === '' || parts.in === '0')) {
      setValueFt('');
    } else if (raw === '' && field === 'in' && (parts.ft === '' || parts.ft === '0')) {
      setValueFt('');
    } else {
      setValueFt((ftSafe + inSafe / 12).toFixed(4));
    }
    setDirty(true);
  }

  function onSimpleChange(raw, kind) {
    if (raw === '') {
      setValueFt('');
    } else if (kind === 'inches') {
      const n = parseFloat(raw);
      if (isNaN(n)) setValueFt('');
      else setValueFt((n / 12).toFixed(4));
    } else {
      setValueFt(raw);
    }
    setDirty(true);
  }

  if (format === 'ft-in') {
    return (
      <div
        className={`field depth-field ${error ? 'field--error' : ''}`}
        ref={fieldRef}
      >
        <label>{label}</label>
        <div className="depth-input-row">
          <input
            type="number"
            value={parts.ft}
            onChange={e => onFtInChange('ft', e.target.value)}
            placeholder="0"
            min="0"
            max={LIMITS.depth.max}
            aria-label="Feet"
          />
          <span className="depth-input-unit">ft</span>
          <input
            type="number"
            step="0.5"
            value={parts.in}
            onChange={e => onFtInChange('in', e.target.value)}
            placeholder="0"
            min="0"
            max="11.9"
            aria-label="Inches"
          />
          <span className="depth-input-unit">in</span>
        </div>
        {error && <div className="field-error">{error}</div>}
      </div>
    );
  }

  if (format === 'inches') {
    return (
      <div
        className={`field depth-field ${error ? 'field--error' : ''}`}
        ref={fieldRef}
      >
        <label>{label}</label>
        <div className="depth-input-row">
          <input
            type="number"
            step="0.5"
            value={parts.totalIn}
            onChange={e => onSimpleChange(e.target.value, 'inches')}
            placeholder="0"
            min="0"
            max={LIMITS.inches.max}
          />
          <span className="depth-input-unit">in</span>
        </div>
        {error && <div className="field-error">{error}</div>}
      </div>
    );
  }

  return (
    <div
      className={`field depth-field ${error ? 'field--error' : ''}`}
      ref={fieldRef}
    >
      <label>{label}</label>
      <div className="depth-input-row">
        <input
          type="number"
          step="0.01"
          value={valueFt}
          onChange={e => onSimpleChange(e.target.value, 'ft-decimal')}
          placeholder="0.0"
          min="0"
          max={LIMITS.depth.max}
        />
        <span className="depth-input-unit">ft</span>
      </div>
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

// ── Tank section ──────────────────────────────────────────────────────────

const DEPTH_FORMATS = [
  { value: 'ft-in',       label: 'Feet & inches', example: '8 ft 3 in' },
  { value: 'ft-decimal',  label: 'Decimal feet',  example: '8.3 ft'    },
  { value: 'inches',      label: 'Inches only',   example: '99 in'     },
];


function TankSection({
  tankNum, name, setName, nameErr,
  cap, setCap, capErr,
  emptyFt, setEmptyFt, emptyFtErr,
  fullFt, setFullFt, fullFtErr,
  tankInfoWarns,
  hasCalPoints,
  setDirty, fieldRefs,
  depthFormat,
  onRecalibrate,
  accent = 'reef',
}) {
  // Usable water = capacity scaled by the drawable band (full - empty).
  // Shown as a callout so the dashboard's "usable gallons" number is explained.
  const _cap = parseFloat(cap);
  const _empty = parseFloat(emptyFt);
  const _full = parseFloat(fullFt);
  const usable = (_cap > 0 && _full > 0 && _full > _empty && _empty >= 0)
    ? {
        gallons: Math.round(_cap * ((_full - _empty) / _full)),
        lost: Math.round(_cap * (_empty / _full)),
        pct: Math.round(((_full - _empty) / _full) * 100),
      }
    : null;

  const [editLevels, setEditLevels] = useState(!!emptyFtErr);
  const fmtLevel = (ftStr) => {
    const f = parseFloat(ftStr);
    if (isNaN(f)) return '—';
    if (depthFormat === 'inches') return `${Math.round(f * 12)} in`;
    if (depthFormat === 'ft-decimal') return `${f.toFixed(1)} ft`;
    const ft = Math.floor(f);
    const inch = Math.round((f - ft) * 12);
    if (inch === 12) return `${ft + 1} ft 0 in`;
    return `${ft} ft ${inch} in`;
  };
  const levelsSummary = `Empty: ${fmtLevel(emptyFt)} · Full: ${fmtLevel(fullFt)}`;

  return (
    <div className="card">
      <Field
        fieldRef={el => { fieldRefs.current[`tank${tankNum}Name`] = el; }}
        label="What do you call this tank?"
        error={nameErr}
        hint="Shows on the dashboard and in alerts."
      >
        <input
          value={name}
          onChange={e => { setName(e.target.value); setDirty(true); }}
          placeholder={tankNum === 1 ? 'e.g. North Tank' : 'e.g. South Tank'}
          maxLength={LIMITS.tankName.max + 5}
        />
      </Field>

      <Field
        fieldRef={el => { fieldRefs.current[`tank${tankNum}Capacity`] = el; }}
        label="Total tank capacity"
        error={capErr}
        hint="The full size printed on the tank or in its spec sheet (e.g. 1,500 gal)."
      >
        <div className="settings-capacity-row">
          <input
            type="number"
            value={cap}
            onChange={e => { setCap(e.target.value); setDirty(true); }}
            placeholder="e.g. 1500"
            min="0"
            max={LIMITS.capacity.max}
          />
          <span className="settings-capacity-unit">gallons</span>
        </div>
      </Field>

      {usable && (
        <div className={`settings-usable-callout ${accent === 'moss' ? 'settings-usable-callout--moss' : ''}`}>
          <span className="settings-usable-callout__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3s-6 6.7-6 11a6 6 0 0 0 12 0c0-4.3-6-11-6-11Z" />
            </svg>
          </span>
          <div className="settings-usable-callout__body">
            <div className="settings-usable-callout__lead">
              Usable capacity: <strong>{usable.gallons.toLocaleString()}</strong> gallons
              <span className="settings-usable-callout__pct">({usable.pct}% of total)</span>
            </div>
            <div className="settings-usable-callout__desc">
              The {usable.lost.toLocaleString()} gallons below the pickup tube can't be drawn. The dashboard shows usable gallons.
            </div>
          </div>
        </div>
      )}

      <div className="divider" />

      {/* Water levels — collapsed summary + Edit (rarely changed) */}
      {!editLevels ? (
        <div className="settings-tank-row">
          <div>
            <div className="settings-tank-row__title">Empty &amp; Full depths</div>
            <div className="settings-tank-row__sub">{levelsSummary}</div>
            {tankInfoWarns && tankInfoWarns.length > 0 && (
              <div className="hint" style={{ marginTop: 4 }}>
                {tankInfoWarns.map((w, i) => (<div key={i}>⚠ {w}</div>))}
              </div>
            )}
          </div>
          <button type="button" className="settings-tank-row__btn" onClick={() => setEditLevels(true)}>Edit</button>
        </div>
      ) : (
        <>
          <div className="settings-tank-row settings-tank-row--open">
            <div>
              <div className="settings-tank-row__title">Empty &amp; Full depths</div>
              <div className="settings-tank-row__sub">What's 0% and what's 100%?</div>
            </div>
            <button type="button" className="settings-tank-row__btn" onClick={() => setEditLevels(false)}>Done</button>
          </div>
          <p className="settings-tank-help">
            These set what 0% and 100% mean on your dashboard. "Empty" is where your pickup tube sits — the lowest usable water. "Full" is the overflow level. Measure from the bottom of the tank.
          </p>

          <DepthInput
            valueFt={emptyFt}
            setValueFt={setEmptyFt}
            format={depthFormat}
            setDirty={setDirty}
            error={emptyFtErr}
            fieldRef={el => { fieldRefs.current[`tank${tankNum}Empty`] = el; }}
            label="Empty depth"
          />
          <DepthInput
            valueFt={fullFt}
            setValueFt={setFullFt}
            format={depthFormat}
            setDirty={setDirty}
            error={fullFtErr}
            fieldRef={el => { fieldRefs.current[`tank${tankNum}Full`] = el; }}
            label="Full depth"
          />

          {!hasCalPoints && (emptyFt || fullFt) && (
            <div className="hint" style={{ marginTop: 4 }}>
              Sensor calibration not set — these depths will be saved, but
              dashboard percent + LED won't activate until calibration is done.
            </div>
          )}
        </>
      )}

      <div className="divider" />

      {/* Sensor calibration — row + Recalibrate button */}
      <div className="settings-tank-row">
        <div>
          <div className="settings-tank-row__title">Sensor calibration</div>
          <div className="settings-tank-row__sub">
            {hasCalPoints
              ? 'Calibration completed at install. Rerun if you replace a sensor or if readings seem inaccurate.'
              : 'Not set up yet. Run calibration so the dashboard can show percent and gallons.'}
          </div>
        </div>
        <button type="button" className="settings-tank-row__btn" onClick={onRecalibrate}>
          {hasCalPoints ? 'Recalibrate' : 'Set up →'}
        </button>
      </div>
    </div>
  );
}

function PrefRadioRow({ label, desc, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`pref-radio-row ${selected ? 'is-selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="pref-radio-row__text">
        <span className="pref-radio-row__label">{label}</span>
        {desc && <span className="pref-radio-row__desc">{desc}</span>}
      </span>
      <span className="pref-radio-row__circle" aria-hidden="true" />
    </button>
  );
}

// ── Main Settings page ────────────────────────────────────────────────────

function ManageDevicesPanel({ devices, onAdd, onRemove, onBack }) {
  return (
    <div className="settings-form">
      <button type="button" className="settings-subpage-back" onClick={onBack}>← Back</button>
      <div className="card manage-devices">
        <div className="manage-devices__head">
          <div className="card-title">Your devices</div>
          <button type="button" className="manage-devices__add" onClick={onAdd}>+ Add a device</button>
        </div>
        <div className="manage-devices__list">
          {devices.map(d => (
            <div key={d.id} className="manage-devices__row">
              <div>
                <div className="manage-devices__name">{d.name}</div>
                <div className="manage-devices__meta">{d.deviceId} · {d.role === 'owner' ? 'Owner' : 'Shared'}</div>
              </div>
              <button type="button" className="manage-devices__remove" onClick={() => onRemove(d)}>
                {d.role === 'owner' ? 'Remove' : 'Remove from my account'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// The address share invites are sent from (TRANSACTIONAL_FROM in the cloud's
// index.js). Shown so owners can tell people what to look for.
const INVITE_FROM = 'hello@sokowatersystems.com';

// "just now", "5 min ago", "3 h ago", then a date. For the pending-invite
// "re-sent" line.
function sinceText(ms) {
  const min = Math.floor((Date.now() - ms) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  if (min < 1440) return `${Math.floor(min / 60)} h ago`;
  return `on ${new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export default function Settings({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { prefs, updatePref } = useUserPrefs(user);

  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    // 'account' is the internal key; 'sharing' is what the tab is called and
    // what every link to it uses. Accept both so an older link still lands.
    if (t === 'sharing' || t === 'account') return 'account';
    if (t === 'plus') return 'plus';
    return t === 'preferences' ? 'preferences' : 'device';
  });
  const [showManage, setShowManage] = useState(false);

  // Devices list — claims from users/{uid}/devices, with device-name from
  // devices/{id} merged in for the picker.
  const [devices, setDevices]     = useState([]);
  const [selected, setSelected]   = useSelectedDevice(user, devices);

  // The full device doc (devices/{selected}) — source of truth for name,
  // tank{1,2}, ledMode, calPoints, etc.
  const [deviceDoc, setDeviceDoc] = useState(null);
  // True once the SELECTED device's doc has loaded (from cache or server).
  // Gates the form so it never flashes empty fields + false "Required"
  // validation in the window after the device list resolves but before the
  // selected device's doc arrives.
  const [deviceReady, setDeviceReady] = useState(false);

  const [loading, setLoading]     = useState(true);
  const slowLoad = useStallRecovery(loading || (selected && !deviceReady));
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  // Split-form dirty tracking (preserved from v1 architecture).
  const [deviceDirty, setDeviceDirty] = useState(false);

  const [error, setError]         = useState('');

  // Device tab form state
  const [deviceName, setDeviceName]       = useState('');
  const [tank1Name, setTank1Name]         = useState('Tank 1');
  const [tank2Name, setTank2Name]         = useState('Tank 2');
  const [tank1Capacity, setTank1Capacity] = useState('');
  const [tank2Capacity, setTank2Capacity] = useState('');
  const [tank1EmptyFt, setTank1EmptyFt]   = useState('');
  const [tank1FullFt, setTank1FullFt]     = useState('');
  const [tank2EmptyFt, setTank2EmptyFt]   = useState('');
  const [tank2FullFt, setTank2FullFt]     = useState('');
  const [ledMode, setLedMode]             = useState('always_on');
  const [ledModeOrig, setLedModeOrig]     = useState('always_on');



  // Account tab — share/release state. shareTarget holds the device
  // object the user clicked Share on; null when the modal is closed.
  // releaseTarget works the same way for the Remove flow.
  const [shareTarget,     setShareTarget]     = useState(null);
  // Share access on a monitor without Plus opens the Plus prompt instead.
  const [plusPromptFor,   setPlusPromptFor]   = useState(null);
  const [shareEmail,      setShareEmail]      = useState('');
  const [shareBusy,       setShareBusy]       = useState(false);
  const [shareError,      setShareError]      = useState('');
  const [shareSuccess,    setShareSuccess]    = useState('');
  const [releaseTarget,   setReleaseTarget]   = useState(null);
  const [releaseBusy,     setReleaseBusy]     = useState(false);
  const [releaseError,    setReleaseError]    = useState('');

  // sharedAccess: map of deviceId → array of { uid, email } for each
  // shared user. Loaded for every owned device; empty array means
  // nobody has been shared with yet. Hidden entirely from the UI
  // when empty so the card stays clean.
  const [sharedAccess, setSharedAccess] = useState({});
  const [pendingShares, setPendingShares] = useState({});

  // Per-email revoke confirmation. revokeTarget is the device, and
  // revokeEmail is who we're about to remove. revokeIsPending
  // differentiates the two flows: false → revoke an accepted share
  // (calls removeShare); true → cancel a pending invite (calls
  // cancelPendingShare). Modal copy and target callable branch on it.
  const [revokeTarget,    setRevokeTarget]    = useState(null);
  const [revokeEmail,     setRevokeEmail]     = useState('');
  const [revokeBusy,      setRevokeBusy]      = useState(false);
  // Resend a pending invite: which row is sending, and the note under it.
  const [resendingKey,    setResendingKey]    = useState(null);
  const [resendNote,      setResendNote]      = useState(null); // { key, ok, text }
  const [revokeError,     setRevokeError]     = useState('');
  const [revokeIsPending, setRevokeIsPending] = useState(false);

  const fieldRefs = useRef({});

  // ── Load user's claimed devices (for the picker) ──────────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const claimsCol = collection(db, 'users', user.uid, 'devices');
    const buildList = (claimsSnap, useCache) => Promise.all(claimsSnap.docs.map(async claimDoc => {
      const claim = { id: claimDoc.id, ...claimDoc.data() };
      const ref = doc(db, 'devices', claim.deviceId);
      const devSnap = useCache ? await cachedDoc(ref) : await getDoc(ref).catch(() => null);
      return {
        ...claim,
        name: (devSnap && devSnap.exists()) ? (devSnap.data().name || claim.deviceId) : claim.deviceId,
        // Plus state (cloud-written): gates Share access.
        plus: (devSnap && devSnap.exists()) ? (devSnap.data().plus || null) : null,
      };
    }));
    (async () => {
      // Cache-first paint, then authoritative server read.
      const cached = await cachedDocs(claimsCol);
      if (cached && !cancelled) { setDevices(await buildList(cached, true)); setLoading(false); }
      try {
        const claimsSnap = await withRetry(() => getDocs(claimsCol));
        if (!cancelled) setDevices(await buildList(claimsSnap, false));
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── Load full device + claim docs when selected changes ───
  useEffect(() => {
    if (!user || !selected) return;
    let cancelled = false;
    setDeviceReady(false);
    const ref = doc(db, 'devices', selected);
    (async () => {
      // Cache-first paint, then authoritative read. Mark ready as soon as we
      // have data (cache or server) so the form shows real values, not empty
      // fields + false "Required" validation.
      const cached = await cachedDoc(ref);
      if (cached && !cancelled) { setDeviceDoc(cached.data()); setDeviceReady(true); }
      try {
        const devSnap = await withRetry(() => getDoc(ref));
        if (!cancelled && devSnap.exists()) setDeviceDoc(devSnap.data());
      } catch (err) {
        console.error('Error loading device/claim:', err);
      } finally {
        if (!cancelled) setDeviceReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, selected]);

  // Back from Stripe Checkout / the billing page (Settings → Plus).
  const plusReturn = usePlusReturn(selected, async (deviceId) => {
    const snap = await getDoc(doc(db, 'devices', deviceId));
    if (!snap.exists()) return null;
    const dev = snap.data();
    if (deviceId === selected) setDeviceDoc((d) => ({ ...(d || {}), plus: dev.plus || null, leakDetection: dev.leakDetection === true }));
    setDevices((ds) => ds.map((x) => (x.deviceId === deviceId ? { ...x, plus: dev.plus || null } : x)));
    return dev;
  });

  // ── Populate form fields when deviceDoc / claimDoc loads ──
  useEffect(() => {
    if (!deviceDoc) return;
    setDeviceName(deviceDoc.name || '');

    const t1 = deviceDoc.tank1 || {};
    const t2 = deviceDoc.tank2 || {};
    const singleTank = (deviceDoc.model || '').startsWith('ST');
    // Single-tank setups have no "Tank 1" concept — the tank IS the location.
    // Autofill its name from the location, and migrate legacy stored 'Tank 1'.
    setTank1Name(
      t1.name && t1.name !== 'Tank 1'
        ? t1.name
        : (singleTank ? (deviceDoc.name || '') : 'Tank 1')
    );
    setTank2Name(t2.name || 'Tank 2');
    setTank1Capacity(t1.capacity > 0 ? String(t1.capacity) : '');
    setTank2Capacity(t2.capacity > 0 ? String(t2.capacity) : '');
    // Strip trailing zeros for display: 9.0000 → "9", 9.5000 → "9.5".
    // Internal precision is preserved on save (parseFloat handles it fine).
    const cleanFt = (n) =>
      typeof n === 'number' ? String(parseFloat(n.toFixed(4))) : '';
    setTank1EmptyFt(cleanFt(t1.emptyFt));
    setTank1FullFt(cleanFt(t1.fullFt));
    setTank2EmptyFt(cleanFt(t2.emptyFt));
    setTank2FullFt(cleanFt(t2.fullFt));

    const mode = deviceDoc.ledMode || 'always_on';
    setLedMode(mode);
    setLedModeOrig(mode);

    setDeviceDirty(false);
  }, [deviceDoc]);


  const hasT2 = deviceDoc?.lastReading?.s2 ?? false;
  // Single-tank product models ("ST") have no tank-2 hardware — hide the
  // whole Tank 2 setup section. Missing model → legacy (dual-capable)
  // behavior. Note hasT2 stays sensor-based: a dual-tank model with a dead
  // sensor 2 still shows the "not detected" hint, which is real info.
  const isSingleTank = (deviceDoc?.model || '').startsWith('ST');
  const hasCal1 = Array.isArray(deviceDoc?.tank1?.calPoints) && deviceDoc.tank1.calPoints.length >= 2;
  const hasCal2 = Array.isArray(deviceDoc?.tank2?.calPoints) && deviceDoc.tank2.calPoints.length >= 2;

  // ── Validation ─────────────────────────────────────────────
  const tank1Info = validateTankInfo(tank1EmptyFt, tank1FullFt);
  const tank2Info = hasT2 ? validateTankInfo(tank2EmptyFt, tank2FullFt) : { ok: true, warns: [] };

  const errors = {
    deviceName:    { msg: validateLocationName(deviceName),  label: 'Location name' },
    tank1Name:     { msg: validateTankName(tank1Name),       label: 'Tank 1 name' },
    tank2Name:     { msg: hasT2 ? validateTankName(tank2Name) : null, label: 'Tank 2 name' },
    tank1Capacity: { msg: validateCapacity(tank1Capacity),   label: 'Tank 1 capacity' },
    tank2Capacity: { msg: hasT2 ? validateCapacity(tank2Capacity) : null, label: 'Tank 2 capacity' },
    tank1Info:     { msg: tank1Info.ok ? null : tank1Info.error, label: 'Tank 1 depths' },
    tank2Info:     { msg: tank2Info.ok ? null : tank2Info.error, label: 'Tank 2 depths' },
  };

  const DEVICE_ERROR_KEYS = [
    'deviceName', 'tank1Name', 'tank2Name',
    'tank1Capacity', 'tank2Capacity',
    'tank1Info', 'tank2Info',
  ];
  const hasDeviceErrors = DEVICE_ERROR_KEYS.some(k => errors[k]?.msg);

  function scrollToField(key) {
    const el = fieldRefs.current[key];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = el.querySelector('input, select, textarea');
    if (input) setTimeout(() => input.focus(), 300);
  }

  // ── Device tab save ────────────────────────────────────────
  // Writes everything device-doc-related: name, tank{N}.{name, capacity,
  // emptyFt, fullFt, emptyMa, fullMa}, ledMode, fastModeUntil.
  // Pair-sync (Rule 1) computes emptyMa/fullMa from feet × calPoints
  // where calPoints exists. Where calPoints is null, mA is left null
  // (tank stays in tank-info-only state).
  async function handleSaveDevice(e) {
    e.preventDefault();
    if (!deviceDoc || !selected) return;
    if (hasDeviceErrors) return;
    setSaving(true);
    setError('');

    try {
      // Build the patch using dot-notation so we don't clobber other
      // tank subfields (calPoints, etc.). Pair-sync runs for each
      // tank's empty/full update.
      const patch = {
        name: deviceName.trim() || deviceDoc.name || selected,
      };

      // Tank 1
      patch['tank1.name'] = tank1Name.trim()
        || (isSingleTank ? (deviceName.trim() || deviceDoc.name || selected) : 'Tank 1');
      patch['tank1.capacity'] = tank1Capacity ? parseFloat(tank1Capacity) : 0;
      const t1Derived = deriveTankInfoFromFeet({
        emptyFt: tank1EmptyFt === '' ? null : parseFloat(tank1EmptyFt),
        fullFt:  tank1FullFt  === '' ? null : parseFloat(tank1FullFt),
      }, deviceDoc.tank1?.calPoints);
      patch['tank1.emptyFt'] = t1Derived.emptyFt;
      patch['tank1.fullFt']  = t1Derived.fullFt;
      patch['tank1.emptyMa'] = t1Derived.emptyMa;
      patch['tank1.fullMa']  = t1Derived.fullMa;

      // Tank 2 — only if present
      if (hasT2) {
        patch['tank2.name'] = tank2Name.trim() || 'Tank 2';
        patch['tank2.capacity'] = tank2Capacity ? parseFloat(tank2Capacity) : 0;
        const t2Derived = deriveTankInfoFromFeet({
          emptyFt: tank2EmptyFt === '' ? null : parseFloat(tank2EmptyFt),
          fullFt:  tank2FullFt  === '' ? null : parseFloat(tank2FullFt),
        }, deviceDoc.tank2?.calPoints);
        patch['tank2.emptyFt'] = t2Derived.emptyFt;
        patch['tank2.fullFt']  = t2Derived.fullFt;
        patch['tank2.emptyMa'] = t2Derived.emptyMa;
        patch['tank2.fullMa']  = t2Derived.fullMa;
      }

      // LED mode
      if (ledMode !== ledModeOrig) {
        patch.ledMode = ledMode;
        patch.ledModeUpdatedAt = serverTimestamp();
      }

      // Fast mode — bump cadence so device picks up new mA values quickly.
      // Cloud function caps server-side at 30 min; we only ever set 2 min
      // for settings edits, well under the cap.
      patch.fastModeUntil = Timestamp.fromMillis(Date.now() + FAST_MODE_SETTINGS_EDIT_MS);

      await updateDoc(doc(db, 'devices', selected), patch);

      setLedModeOrig(ledMode);
      setSaved(true);
      setDeviceDirty(false);
      setTimeout(() => setSaved(false), 3000);

      // Reload device doc to reflect saved state in form (including
      // any cloud-side cleanups the cloud function might have done).
      const snap = await getDoc(doc(db, 'devices', selected));
      if (snap.exists()) setDeviceDoc(snap.data());
    } catch (err) {
      console.error('Save device error:', err);
      setError('Could not save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // ── LED mode: auto-save immediately (Preferences tab has no Save button) ──
  async function saveLedMode(mode) {
    setLedMode(mode);
    if (!selected) return;
    try {
      await updateDoc(doc(db, 'devices', selected), {
        ledMode: mode,
        ledModeUpdatedAt: serverTimestamp(),
        fastModeUntil: Timestamp.fromMillis(Date.now() + FAST_MODE_SETTINGS_EDIT_MS),
      });
      setLedModeOrig(mode);
    } catch (err) {
      console.error('LED save failed:', err);
    }
  }

  async function reloadDevicesList() {
    if (!user) return;
    try {
      const claimsSnap = await getDocs(
        collection(db, 'users', user.uid, 'devices')
      );
      const list = await Promise.all(claimsSnap.docs.map(async claimDoc => {
        const claim = { id: claimDoc.id, ...claimDoc.data() };
        try {
          const devSnap = await getDoc(doc(db, 'devices', claim.deviceId));
          return {
            ...claim,
            name: devSnap.exists() ? (devSnap.data().name || claim.deviceId) : claim.deviceId,
            plus: devSnap.exists() ? (devSnap.data().plus || null) : null,
          };
        } catch (e) {
          return { ...claim, name: claim.deviceId };
        }
      }));
      setDevices(list);
    } catch (err) {
      console.error('reloadDevicesList:', err);
    }
  }

  // ── Load the "shared with" list for each device the user owns ──
  // Calls the listShares cloud callable for each owned device, since
  // a direct collection-group query is blocked by rules (rules can't
  // approve reads across other users' claim subcollections, which is
  // correct — the callable uses Admin SDK to bypass).
  //
  // Cost: one callable invocation per owned device. Negligible at
  // the scale of 1-5 owned devices per user.
  async function reloadSharedAccess(devicesList) {
    if (!user || !devicesList) return;
    const ownedDeviceIds = devicesList
      .filter(d => d.role === 'owner')
      .map(d => d.deviceId);

    if (ownedDeviceIds.length === 0) {
      setSharedAccess({});
      return;
    }

    const listShares = httpsCallable(functions, 'listShares');
    const result = {};
    await Promise.all(ownedDeviceIds.map(async deviceId => {
      try {
        const res = await listShares({ deviceId });
        result[deviceId] = (res?.data?.shares) || [];
      } catch (err) {
        console.error(`reloadSharedAccess[${deviceId}]:`, err);
        result[deviceId] = [];
      }
    }));
    setSharedAccess(result);
  }

  // Load shared-access lists whenever the devices list changes
  // (initial load, after share, after revoke, after release).
  useEffect(() => {
    reloadSharedAccess(devices);
    reloadPendingShares(devices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  // ── Load the "pending invites" list for each device the user owns ──
  // Mirrors reloadSharedAccess. Calls the listPendingShares callable
  // (pendingShares is fully denied to clients at the rules level, so
  // a callable using Admin SDK is the only owner-readable path).
  //
  // Result shape: { [deviceId]: [{ email, createdAt }, ...] }
  async function reloadPendingShares(devicesList) {
    if (!user || !devicesList) return;
    const ownedDeviceIds = devicesList
      .filter(d => d.role === 'owner')
      .map(d => d.deviceId);

    if (ownedDeviceIds.length === 0) {
      setPendingShares({});
      return;
    }

    const listPendingShares = httpsCallable(functions, 'listPendingShares');
    const result = {};
    await Promise.all(ownedDeviceIds.map(async deviceId => {
      try {
        const res = await listPendingShares({ deviceId });
        result[deviceId] = (res?.data?.pending) || [];
      } catch (err) {
        console.error(`reloadPendingShares[${deviceId}]:`, err);
        result[deviceId] = [];
      }
    }));
    setPendingShares(result);
  }

  // ── Share access — call shareDevice callable ──────────────
  async function handleShareSubmit(e) {
    e.preventDefault();
    if (!shareTarget) return;
    setShareError('');
    setShareSuccess('');
    const email = shareEmail.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setShareError('Please enter a valid email address.');
      return;
    }
    setShareBusy(true);
    try {
      const shareDevice = httpsCallable(functions, 'shareDevice');
      const result = await shareDevice({
        deviceId: shareTarget.deviceId,
        recipientEmail: email,
      });
      const data = result?.data || {};
      if (data.alreadyShared) {
        setShareSuccess(`${email} has already been invited or has access.`);
      } else {
        setShareSuccess(`Invitation sent to ${email} from ${INVITE_FROM}.`);
      }
      setShareEmail('');
      // Refresh the appropriate list immediately so the new entry
      // appears on the card. If recipient has an account, a claim
      // doc was written (refresh Shared with). Otherwise a pending
      // invite was written (refresh Pending invites).
      if (!data.alreadyShared) {
        if (data.recipientExists) {
          reloadSharedAccess(devices);
        } else {
          reloadPendingShares(devices);
        }
      }
    } catch (err) {
      console.error('shareDevice failed:', err);
      const code = err?.code || '';
      const msg  = err?.message || '';
      if (code === 'functions/permission-denied' || code === 'permission-denied') {
        setShareError("Only the device's owner can share access.");
      } else if (code === 'functions/invalid-argument' || code === 'invalid-argument') {
        setShareError(msg || 'Please check the email address.');
      } else if (code === 'functions/not-found' || code === 'not-found') {
        setShareError('Device not found.');
      } else {
        setShareError('Something went wrong. Please try again.');
      }
    } finally {
      setShareBusy(false);
    }
  }

  function closeShareModal() {
    setShareTarget(null);
    setShareEmail('');
    setShareError('');
    setShareSuccess('');
  }

  // ── Revoke one shared user's access OR cancel a pending invite ──
  // Owner-only flow. Branches on revokeIsPending:
  //   false → calls removeShare (deletes a shared claim doc)
  //   true  → calls cancelPendingShare (deletes a pendingShares doc)
  // Both refresh the appropriate list afterward.
  async function handleConfirmRevoke() {
    if (!revokeTarget || !revokeEmail) return;
    setRevokeError('');
    setRevokeBusy(true);
    try {
      if (revokeIsPending) {
        const cancelPendingShare = httpsCallable(functions, 'cancelPendingShare');
        await cancelPendingShare({
          deviceId: revokeTarget.deviceId,
          recipientEmail: revokeEmail,
        });
        closeRevokeModal();
        // Refresh the pending-invites list so the cancelled entry disappears.
        await reloadPendingShares(devices);
      } else {
        const removeShare = httpsCallable(functions, 'removeShare');
        await removeShare({
          deviceId: revokeTarget.deviceId,
          recipientEmail: revokeEmail,
        });
        closeRevokeModal();
        // Refresh the shared-with list so the revoked entry disappears.
        await reloadSharedAccess(devices);
      }
    } catch (err) {
      console.error(
        revokeIsPending ? 'cancelPendingShare failed:' : 'removeShare failed:',
        err
      );
      const code = err?.code || '';
      const msg  = err?.message || '';
      if (code === 'functions/permission-denied' || code === 'permission-denied') {
        setRevokeError("Only the device's owner can manage sharing.");
      } else if (code === 'functions/failed-precondition' || code === 'failed-precondition') {
        setRevokeError(msg || 'Cannot remove that access.');
      } else {
        setRevokeError(
          revokeIsPending
            ? 'Could not cancel that invitation. Please try again.'
            : 'Could not remove access. Please try again.'
        );
      }
    } finally {
      setRevokeBusy(false);
    }
  }

  // ── Resend a pending invite's email (owner only) ──
  // For "they never got it": usually spam or deleted unread. The invite itself
  // is unchanged. The cloud refuses a second send within 10 minutes and caps
  // resends per invite; its message is shown as-is.
  async function handleResendInvite(device, email) {
    const key = `${device.deviceId}|${email}`;
    setResendingKey(key);
    setResendNote(null);
    try {
      const resendPendingShare = httpsCallable(functions, 'resendPendingShare');
      const res = await resendPendingShare({ deviceId: device.deviceId, recipientEmail: email });
      setResendNote({
        key, ok: true,
        text: res?.data?.alreadyGone
          ? 'This invitation was already accepted or cancelled.'
          : `Sent again. Ask them to check their spam folder too.`,
      });
      await reloadPendingShares(devices);
    } catch (err) {
      console.error('resendPendingShare failed:', err);
      setResendNote({ key, ok: false, text: err?.message || 'Could not resend. Please try again.' });
    } finally {
      setResendingKey(null);
    }
  }

  function closeRevokeModal() {
    setRevokeTarget(null);
    setRevokeEmail('');
    setRevokeError('');
    setRevokeIsPending(false);
  }

  // ── Release / remove device ───────────────────────────────
  // Owners: call releaseDevice (deletes all claim docs + clears
  // ownership). Shared users: client-delete their own claim doc
  // (rules allow this when role === 'shared').
  async function handleConfirmRelease() {
    if (!releaseTarget) return;
    setReleaseError('');
    setReleaseBusy(true);
    try {
      if (releaseTarget.role === 'owner') {
        const releaseDevice = httpsCallable(functions, 'releaseDevice');
        await releaseDevice({ deviceId: releaseTarget.deviceId });
      } else {
        await deleteDoc(
          doc(db, 'users', user.uid, 'devices', releaseTarget.deviceId)
        );
      }
      // If we just removed the currently-selected device, switch the
      // selection so the rest of Settings doesn't try to render
      // against a doc that no longer exists.
      if (selected === releaseTarget.deviceId) {
        setSelected(null);
      }
      setReleaseTarget(null);
      await reloadDevicesList();
    } catch (err) {
      console.error('releaseDevice failed:', err);
      setReleaseError('Could not remove device. Please try again.');
    } finally {
      setReleaseBusy(false);
    }
  }

  function closeReleaseModal() {
    setReleaseTarget(null);
    setReleaseError('');
  }

  // ── Render ────────────────────────────────────────────────
  // Keep the spinner up until BOTH the device list AND (if a device is
  // selected) that device's doc have loaded — otherwise the form flashes
  // empty fields with false "Required" validation before the data arrives.
  if (loading || (selected && !deviceReady)) {
    return (
      <div className="app-container">
        <TopBar subtitle="Settings" />
        <div className="loading-page">
          <div className="spinner" />
          {slowLoad && (
            <button type="button" className="btn btn-primary" style={{ marginTop: 16 }}
              onClick={() => window.location.reload()}>
              Reload
            </button>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  const behaviorChanged = ledMode !== ledModeOrig;

  return (
    <div className="app-container">
      <TopBar subtitle={devices.find(d => d.deviceId === selected)?.name || 'Settings'} />
      <div className="page-content">

        {/* Toolbar — device picker (left) + Add/remove device (right) */}
        <div className="settings-toolbar">
          <div className="settings-toolbar__devices">
            {devices.length > 1 && (
              <div className="device-selector">
                {devices.map(d => (
                  <button
                    key={d.deviceId}
                    type="button"
                    className={`device-pill ${selected === d.deviceId ? 'active' : ''}`}
                    onClick={() => setSelected(d.deviceId)}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className={`settings-manage-pill ${showManage ? 'active' : ''}`}
            onClick={() => setShowManage(v => !v)}
          >
            + Add/remove device
          </button>
        </div>

        {showManage ? (
          <ManageDevicesPanel
            devices={devices}
            onAdd={() => navigate('/devices')}
            onRemove={(d) => { setReleaseError(''); setReleaseTarget(d); }}
            onBack={() => setShowManage(false)}
          />
        ) : (
        <>
        <div className="settings-tabs">
          <button type="button" className={`settings-tab ${activeTab === 'device' ? 'active' : ''}`} onClick={() => setActiveTab('device')}>Tank</button>
          <button type="button" className={`settings-tab ${activeTab === 'preferences' ? 'active' : ''}`} onClick={() => setActiveTab('preferences')}>Preferences</button>
          <button type="button" className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`} onClick={() => setActiveTab('account')}>Sharing</button>
          <button type="button" className={`settings-tab ${activeTab === 'plus' ? 'active' : ''}`} onClick={() => setActiveTab('plus')}>SoKo Plus</button>
        </div>

        {saved && (
          <div className="toast">
            <span>✓</span>
            <span>Settings saved · Device will update within 60s</span>
          </div>
        )}

        {error && (
          <div className="alert-banner fault">
            <div className="alert-banner-title">Couldn't save</div>
            <div className="alert-banner-desc">{error}</div>
          </div>
        )}

        {/* Validation summary for the Device tab */}
        {activeTab === 'device' && hasDeviceErrors && (
          <div className="validation-summary">
            <div className="validation-summary__title">
              {DEVICE_ERROR_KEYS.filter(k => errors[k]?.msg).length === 1
                ? '1 field needs attention'
                : `${DEVICE_ERROR_KEYS.filter(k => errors[k]?.msg).length} fields need attention`}
            </div>
            <div className="validation-summary__list">
              {DEVICE_ERROR_KEYS.filter(k => errors[k]?.msg).map(key => (
                <button
                  key={key}
                  type="button"
                  className="validation-summary__item"
                  onClick={() => scrollToField(key)}
                >
                  {errors[key].label}: <span className="validation-summary__msg">{errors[key].msg}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── DEVICE TAB ─────────────────────────────────────────────── */}
        {activeTab === 'device' && (
          <form id="device-form" onSubmit={handleSaveDevice} className="settings-form">

            <div className="section-label">Location</div>
            <div className="card">
              <Field
                fieldRef={el => { fieldRefs.current.deviceName = el; }}
                label="Location name"
                error={errors.deviceName.msg}
              >
                <input
                  value={deviceName}
                  onChange={e => { setDeviceName(e.target.value); setDeviceDirty(true); }}
                  placeholder="Home"
                  maxLength={LIMITS.locationName.max + 5}
                />
              </Field>
            </div>

            <div className="settings-tank-header">
              <h2 className="settings-tank-title">{tank1Name || (isSingleTank ? (deviceName || 'Tank 1') : 'Tank 1')}</h2>
              <span className={`badge ${hasCal1 ? 'badge-reef' : 'badge-gray'}`}>{hasCal1 ? 'Calibrated' : 'Setup needed'}</span>
            </div>
            <TankSection
              tankNum={1}
              name={tank1Name}            setName={setTank1Name}        nameErr={errors.tank1Name.msg}
              cap={tank1Capacity}         setCap={setTank1Capacity}     capErr={errors.tank1Capacity.msg}
              emptyFt={tank1EmptyFt}      setEmptyFt={setTank1EmptyFt}  emptyFtErr={errors.tank1Info.msg}
              fullFt={tank1FullFt}        setFullFt={setTank1FullFt}    fullFtErr={null}
              tankInfoWarns={tank1Info.warns}
              hasCalPoints={hasCal1}
              setDirty={setDeviceDirty}
              fieldRefs={fieldRefs}
              depthFormat={prefs.depthFormat}
              onRecalibrate={() => navigate(`/setup/${selected}/1`, { state: { returnTo: '/settings', recalibrate: true } })}
            />

            {!isSingleTank && (<>
            <div className="settings-tank-header">
              <h2 className="settings-tank-title">{tank2Name || 'Tank 2'}</h2>
              <span className={`badge ${hasCal2 ? 'badge-moss' : 'badge-gray'}`}>{hasCal2 ? 'Calibrated' : 'Setup needed'}</span>
            </div>
            {hasT2 ? (
              <TankSection
                tankNum={2}
                name={tank2Name}            setName={setTank2Name}        nameErr={errors.tank2Name.msg}
                cap={tank2Capacity}         setCap={setTank2Capacity}     capErr={errors.tank2Capacity.msg}
                emptyFt={tank2EmptyFt}      setEmptyFt={setTank2EmptyFt}  emptyFtErr={errors.tank2Info.msg}
                fullFt={tank2FullFt}        setFullFt={setTank2FullFt}    fullFtErr={null}
                tankInfoWarns={tank2Info.warns}
                hasCalPoints={hasCal2}
                accent="moss"
                setDirty={setDeviceDirty}
                fieldRefs={fieldRefs}
                depthFormat={prefs.depthFormat}
                onRecalibrate={() => navigate(`/setup/${selected}/2`, { state: { returnTo: '/settings', recalibrate: true } })}
              />
            ) : (
              <div className="card">
                <div className="settings-hint">
                  Tank 2 not detected — reboot device to detect a newly connected sensor
                </div>
              </div>
            )}
            </>)}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || hasDeviceErrors}
              style={{ maxWidth: '560px' }}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>

            {deviceDirty && !hasDeviceErrors && (
              <div className="unsaved-bar">
                <div className="unsaved-bar__text">
                  <span className="unsaved-bar__msg">Unsaved changes</span>
                  {behaviorChanged && (
                    <span className="unsaved-bar__sub">
                      ⏱ Device updates within 60s
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  form="device-form"
                  disabled={saving}
                  className="unsaved-bar__btn"
                >
                  {saving ? 'Saving…' : 'Save now'}
                </button>
              </div>
            )}

          </form>
        )}

        {/* ── PREFERENCES TAB ─────────────────────────────────────────── */}
        {/* ── Plus tab — one monitor at a time (the pills above pick it). ── */}
        {activeTab === 'plus' && (
          <div className="settings-plus">
            <PlusCard
              deviceId={selected}
              plus={deviceDoc?.plus}
              isOwner={devices.find(d => d.deviceId === selected)?.role === 'owner'}
              returning={plusReturn}
            />
            {devices.length > 1 && (
              <p className="hint settings-plus__note">SoKo Plus is per monitor. Pick a monitor above to see its plan.</p>
            )}
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="settings-form">
            {/* Display */}
            <div className="card">
              <div className="card-title">Display</div>
              <div className="settings-card-sub">How depth shows up across the app.</div>
              <div className="divider" />
              <div className="pref-radio-label">Show depths as</div>
              {DEPTH_FORMATS.map(f => (
                <PrefRadioRow
                  key={f.value}
                  label={f.label}
                  desc={f.example}
                  selected={prefs.depthFormat === f.value}
                  onSelect={() => { updatePref('depthFormat', f.value); }}
                />
              ))}
            </div>

            {/* LED bar */}
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-title">LED water level gauge</div>
              <div className="settings-card-sub">When should the lights on your monitor light up?</div>
              <div className="divider" />
              {LED_MODES.map(m => (
                <PrefRadioRow
                  key={m.value}
                  label={m.label}
                  desc={m.description}
                  selected={ledMode === m.value}
                  onSelect={() => saveLedMode(m.value)}
                />
              ))}
              {/* A Free monitor checks in hourly, so a change waits for that
                  unless they wake it. */}
              {deviceDoc && tierOf(deviceDoc) === 'free' && (
                <p className="hint" style={{ marginTop: 10 }}>
                  Changes apply within an hour. Give the button on your monitor a short press to apply them right away.
                </p>
              )}
            </div>

            {/* Appearance */}
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-title">Appearance</div>
              <div className="settings-card-sub">Switch between light and dark mode.</div>
              <div className="divider" />
              <AppearancePills />
            </div>

            <p className="alerts-autosave-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
              Changes save automatically.
            </p>
          </div>
        )}

        {/* ── SHARING TAB (key 'account' for back-compat) ─────────────── */}
        {activeTab === 'account' && (
          <div className="settings-form">

            {/* Account details (email address, confirmation) are NOT here:
                they live in the account modal behind the avatar at the top of
                every screen. This tab is about letting other people see your
                tanks, so sharing is the first thing on it. */}
            <div className="section-label">Share access</div>
            <p className="hint" style={{ marginTop: -4, marginBottom: 12 }}>
              Let friends or family see your tank levels. They sign in with
              their own account and set up their own alerts.
            </p>
            {devices.length === 0 ? (
              <div className="card">
                <p className="hint" style={{ margin: 0 }}>
                  You don't have any devices yet. Add one from the Devices page.
                </p>
              </div>
            ) : (
              devices.map(d => {
                const isOwner = d.role === 'owner';
                const shares = isOwner ? (sharedAccess[d.deviceId] || []) : [];
                return (
                  <div key={d.id} className="card" style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                      <div>
                        <div className="settings-account-email" style={{ marginBottom: 2 }}>
                          {d.name}
                        </div>
                        <div className="hint" style={{ margin: 0 }}>
                          {isOwner ? 'Owner' : 'Shared with you'} · {d.deviceId}
                        </div>
                      </div>
                    </div>

                    {isOwner && shares.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div className="hint" style={{ marginBottom: 6, fontWeight: 600 }}>
                          Shared with
                        </div>
                        {shares.map(s => (
                          <div
                            key={s.uid}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 0',
                              borderBottom: '1px solid var(--border, #e3ddd0)',
                              gap: 8,
                            }}
                          >
                            <span style={{ fontSize: 14, wordBreak: 'break-all' }}>
                              {s.email}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setRevokeError('');
                                setRevokeTarget(d);
                                setRevokeEmail(s.email);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--accent, #0e3a4a)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              Revoke access
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {isOwner && (pendingShares[d.deviceId] || []).length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div className="hint" style={{ fontWeight: 600 }}>
                          Pending invites
                        </div>
                        <div className="hint" style={{ fontSize: 12, marginBottom: 6 }}>
                          Invites come from {INVITE_FROM}.
                        </div>
                        {(pendingShares[d.deviceId] || []).map(p => (
                          <div
                            key={p.email}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 0',
                              borderBottom: '1px solid var(--border, #e3ddd0)',
                              gap: 8,
                            }}
                          >
                            <span style={{ fontSize: 14, wordBreak: 'break-all' }}>
                              <span>{p.email}</span>
                              <span
                                className="hint"
                                style={{ display: 'block', fontSize: 12, marginTop: 2 }}
                              >
                                {p.lastResentAt ? `Invitation re-sent ${sinceText(p.lastResentAt)}` : 'Invitation sent'}
                              </span>
                              {resendNote && resendNote.key === `${d.deviceId}|${p.email}` && (
                                <span
                                  role="status"
                                  style={{ display: 'block', fontSize: 12, marginTop: 4,
                                    color: resendNote.ok ? 'var(--color-reef, #2E7A7F)' : 'var(--color-copper, #B8552A)' }}
                                >
                                  {resendNote.text}
                                </span>
                              )}
                            </span>
                            <span style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
                            <button
                              type="button"
                              disabled={resendingKey === `${d.deviceId}|${p.email}`}
                              onClick={() => handleResendInvite(d, p.email)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--accent, #0e3a4a)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              {resendingKey === `${d.deviceId}|${p.email}` ? 'Sending…' : 'Resend'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRevokeError('');
                                setRevokeTarget(d);
                                setRevokeEmail(p.email);
                                setRevokeIsPending(true);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: 'var(--accent, #0e3a4a)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                font: 'inherit',
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              Cancel
                            </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                      {isOwner && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ width: 'auto', padding: '8px 16px', marginTop: 0 }}
                          onClick={() => {
                            if (!plusIsOn(d.plus)) { setPlusPromptFor(d); return; }
                            setShareError('');
                            setShareSuccess('');
                            setShareEmail('');
                            setShareTarget(d);
                          }}
                        >
                          Share access
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ width: 'auto', padding: '8px 16px', marginTop: 0 }}
                        onClick={() => {
                          setReleaseError('');
                          setReleaseTarget(d);
                        }}
                      >
                        {isOwner ? 'Remove device' : 'Remove from my account'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            <div className="section-label">About</div>
            <div className="card">
              <div className="settings-about">
                <div>
                  <div className="settings-about__name">SoKo Water Systems</div>
                  <div className="settings-about__version">Dashboard v{APP_VERSION}</div>
                </div>
              </div>
            </div>

          </div>
        )}
        </>
        )}

        {/* ── Plus prompt (Share access on a monitor without Plus) ── */}
        <PlusPromptModal
          open={!!plusPromptFor}
          onClose={() => setPlusPromptFor(null)}
          deviceId={plusPromptFor?.deviceId}
          plus={plusPromptFor?.plus}
          isOwner={plusPromptFor?.role === 'owner'}
          reason="sharing"
        />

        {/* ── Share access modal ─────────────────────────────── */}
        {shareTarget && (
          <div
            className="modal-backdrop"
            onClick={closeShareModal}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100, padding: 16,
            }}
          >
            <div
              className="card"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 440, width: '100%', margin: 0 }}
            >
              <h3 style={{ marginTop: 0 }}>Share "{shareTarget.name}"</h3>
              <p className="hint" style={{ marginTop: 0 }}>
                The person you share with will have full access to this device,
                including water levels, calibration, and alert settings.
                They cannot share it with others or remove it from your account.
              </p>

              {shareError && (
                <div className="alert-banner fault">
                  <div className="alert-banner-desc">{shareError}</div>
                </div>
              )}
              {shareSuccess && (
                <div className="toast" style={{ marginBottom: 12 }}>
                  <span>✓</span> {shareSuccess}
                </div>
              )}

              <form onSubmit={handleShareSubmit}>
                <div className="field">
                  <label>Email address</label>
                  <input
                    type="email"
                    placeholder="friend@example.com"
                    value={shareEmail}
                    onChange={e => setShareEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={shareBusy}
                    style={{ width: 'auto', flex: 1, marginTop: 0 }}
                  >
                    {shareBusy ? 'Sharing…' : 'Share'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeShareModal}
                    style={{ width: 'auto', flex: 1, marginTop: 0 }}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Remove device modal ───────────────────────────── */}
        {releaseTarget && (
          <div
            className="modal-backdrop"
            onClick={closeReleaseModal}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100, padding: 16,
            }}
          >
            <div
              className="card"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 440, width: '100%', margin: 0 }}
            >
              <h3 style={{ marginTop: 0 }}>
                Remove {releaseTarget.name}?
              </h3>
              <p style={{ marginTop: 0 }}>
                {releaseTarget.role === 'owner'
                  ? 'This will remove the device from your account and from anyone you\'ve shared it with. You can re-claim it later using the code from the card in your box.'
                  : "This will remove your access to this device. The owner can share it with you again later."}
              </p>

              {releaseError && (
                <div className="alert-banner fault">
                  <div className="alert-banner-desc">{releaseError}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmRelease}
                  disabled={releaseBusy}
                  style={{ width: 'auto', flex: 1, marginTop: 0 }}
                >
                  {releaseBusy ? 'Removing…' : (releaseTarget.role === 'owner' ? 'Remove device' : 'Remove access')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeReleaseModal}
                  disabled={releaseBusy}
                  style={{ width: 'auto', flex: 1, marginTop: 0 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Revoke shared-user access modal ────────────────── */}
        {revokeTarget && revokeEmail && (
          <div
            className="modal-backdrop"
            onClick={closeRevokeModal}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100, padding: 16,
            }}
          >
            <div
              className="card"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 440, width: '100%', margin: 0 }}
            >
              <h3 style={{ marginTop: 0 }}>
                {revokeIsPending
                  ? `Cancel invitation to ${revokeEmail}?`
                  : `Remove access for ${revokeEmail}?`}
              </h3>
              <p style={{ marginTop: 0 }}>
                {revokeIsPending ? (
                  <>
                    They will not be granted access to <strong>{revokeTarget.name}</strong> when
                    they create an account. The invitation email already sent cannot be recalled.
                    You can invite them again later.
                  </>
                ) : (
                  <>
                    They will no longer see <strong>{revokeTarget.name}</strong> in their dashboard
                    or receive alerts for it. You can share access with them again later.
                  </>
                )}
              </p>

              {revokeError && (
                <div className="alert-banner fault">
                  <div className="alert-banner-desc">{revokeError}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmRevoke}
                  disabled={revokeBusy}
                  style={{ width: 'auto', flex: 1, marginTop: 0 }}
                >
                  {revokeBusy
                    ? (revokeIsPending ? 'Cancelling…' : 'Removing…')
                    : (revokeIsPending ? 'Cancel invitation' : 'Remove access')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeRevokeModal}
                  disabled={revokeBusy}
                  style={{ width: 'auto', flex: 1, marginTop: 0 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <BottomNav />
    </div>
  );
}